import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  App,
  Empty,
} from 'antd';
import { PlusOutlined, ReloadOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { axios } from '../../lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const CATALOG_DEFAULTS = {
  STARTER: {
    planName: 'STARTER',
    amount: 699,
    validity: '1 month',
    maxActiveCoupons: 5,
    maxShops: 5,
    happyHoursPerMonth: 2,
    luckyDrawsPerMonth: 2,
    maxStaff: 1,
    aiCampaignSuggestions: false,
    prioritySupport: false,
    featuredPlacement: false,
    sortOrder: 1,
    highlighted: false,
  },
  GROW: {
    planName: 'GROW',
    amount: 999,
    validity: '1 month',
    maxActiveCoupons: 15,
    maxShops: 20,
    happyHoursPerMonth: 8,
    luckyDrawsPerMonth: 5,
    maxStaff: 2,
    aiCampaignSuggestions: true,
    prioritySupport: true,
    featuredPlacement: false,
    sortOrder: 2,
    highlighted: true,
  },
  PRO: {
    planName: 'PRO',
    amount: 1999,
    validity: '1 month',
    maxActiveCoupons: -1,
    maxShops: 50,
    happyHoursPerMonth: -1,
    luckyDrawsPerMonth: -1,
    maxStaff: 3,
    aiCampaignSuggestions: true,
    prioritySupport: true,
    featuredPlacement: true,
    sortOrder: 3,
    highlighted: false,
  },
};

const limitLabel = (v) => (v == null ? '—' : v < 0 ? 'Unlimited' : String(v));

const boolTag = (ok, yes = 'Yes', no = 'No') =>
  ok ? <Tag color="green">{yes}</Tag> : <Tag>{no}</Tag>;

const PlanManagement = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/razorpay/plans');
      const data = Array.isArray(response)
        ? response
        : response?.data || response?.plans || [];
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const filteredPlans = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter((p) => {
      const hay = `${p.name || ''} ${p.tier || ''} ${p.plan_code || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [plans, search]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      validity: '1 month',
      maxActiveCoupons: 5,
      maxShops: 5,
      happyHoursPerMonth: 2,
      luckyDrawsPerMonth: 2,
      maxStaff: 1,
      sortOrder: 0,
      aiCampaignSuggestions: false,
      prioritySupport: false,
      featuredPlacement: false,
      highlighted: false,
    });
    setCreateOpen(true);
  };

  const loadCatalog = (tier) => {
    const defaults = CATALOG_DEFAULTS[tier];
    if (!defaults) return;
    form.setFieldsValue({ tier, ...defaults });
  };

  const createPlan = async (values) => {
    setCreating(true);
    try {
      await axios.post('/razorpay/create-plan', {
        name: values.planName,
        price: Number(values.amount),
        validity: values.validity || '1 month',
        tier: values.tier || undefined,
        plan_code: values.plan_code?.trim() || undefined,
        maxActiveCoupons: values.maxActiveCoupons,
        maxShops: values.maxShops,
        happyHoursPerMonth: values.happyHoursPerMonth,
        luckyDrawsPerMonth: values.luckyDrawsPerMonth,
        maxStaff: values.maxStaff,
        aiCampaignSuggestions: !!values.aiCampaignSuggestions,
        prioritySupport: !!values.prioritySupport,
        featuredPlacement: !!values.featuredPlacement,
        sortOrder: values.sortOrder ?? 0,
        highlighted: !!values.highlighted,
      });
      message.success('Plan created successfully');
      setCreateOpen(false);
      form.resetFields();
      await fetchPlans();
    } catch (error) {
      console.error('Error creating plan:', error);
    } finally {
      setCreating(false);
    }
  };

  const seedPlans = async () => {
    setSeeding(true);
    try {
      await axios.post('/razorpay/seed-plans', {});
      message.success('STARTER / GROW / PRO catalog seeded');
      await fetchPlans();
    } catch (error) {
      console.error('Error seeding plans:', error);
    } finally {
      setSeeding(false);
    }
  };

  const showDetail = (record) => {
    setSelected(record);
    setDetailOpen(true);
  };

  const columns = [
    {
      title: 'Plan',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <strong>{name}</strong>
            {record.tier && <Tag color="blue">{record.tier}</Tag>}
            {record.highlighted && <Tag color="gold">Popular</Tag>}
          </Space>
          <span style={{ color: colors.mutedSoft, fontSize: 12 }}>{record.plan_code}</span>
        </Space>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price, record) => (
        <span>
          ₹{price}
          <div style={{ color: colors.mutedSoft, fontSize: 12 }}>/ {record.validity}</div>
        </span>
      ),
    },
    {
      title: 'Coupons',
      dataIndex: 'maxActiveCoupons',
      key: 'maxActiveCoupons',
      width: 100,
      render: limitLabel,
    },
    {
      title: 'Shops',
      dataIndex: 'maxShops',
      key: 'maxShops',
      width: 90,
      render: limitLabel,
    },
    {
      title: 'HH / mo',
      dataIndex: 'happyHoursPerMonth',
      key: 'happyHoursPerMonth',
      width: 90,
      render: limitLabel,
    },
    {
      title: 'LD / mo',
      dataIndex: 'luckyDrawsPerMonth',
      key: 'luckyDrawsPerMonth',
      width: 90,
      render: limitLabel,
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          {record.aiCampaignSuggestions && <Tag color="purple">AI</Tag>}
          {record.prioritySupport && <Tag color="cyan">Priority</Tag>}
          {record.featuredPlacement && <Tag color="magenta">Featured</Tag>}
          {!record.aiCampaignSuggestions &&
            !record.prioritySupport &&
            !record.featuredPlacement && <Tag>Basic</Tag>}
        </Space>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          onClick={() => showDetail(record)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Plan Management</h2>
          <p style={{ margin: '6px 0 0', color: colors.muted }}>
            Manage STARTER, GROW, and PRO subscription plans, limits, and feature flags.
          </p>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchPlans} loading={loading}>
            Refresh
          </Button>
          <Button
            icon={<CloudUploadOutlined />}
            loading={seeding}
            onClick={seedPlans}
          >
            Seed Catalog
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={openCreate}
          >
            Create Plan
          </Button>
        </Space>
      </div>

      <Card
        style={{ marginTop: 16 }}
        bodyStyle={{ paddingTop: 16 }}
      >
        <div className="row row_searchbar" style={{ marginTop: 0 }}>
          <Input.Search
            placeholder="Search by name, tier, or plan code"
            allowClear
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        </div>

        <Table
          rowKey={(r) => r._id || r.plan_code}
          loading={loading}
          columns={columns}
          dataSource={filteredPlans}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => showDetail(record),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <Empty
                description="No plans yet. Seed the catalog or create a plan."
                style={{ padding: 24 }}
              >
                <Space>
                  <Button onClick={seedPlans} loading={seeding}>
                    Seed STARTER / GROW / PRO
                  </Button>
                  <Button type="primary" onClick={openCreate}>
                    Create Plan
                  </Button>
                </Space>
              </Empty>
            ),
          }}
        />
      </Card>

      <Modal
        title="Create Plan"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
        style={{ top: 24 }}
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <span style={{ color: colors.muted }}>Load from catalog:</span>
          {['STARTER', 'GROW', 'PRO'].map((tier) => (
            <Button key={tier} size="small" onClick={() => loadCatalog(tier)}>
              {tier}
            </Button>
          ))}
        </Space>

        <Form form={form} layout="vertical" onFinish={createPlan}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            <Form.Item
              label="Plan Name"
              name="planName"
              rules={[{ required: true, message: 'Enter plan name' }]}
            >
              <Input placeholder="e.g. STARTER" />
            </Form.Item>
            <Form.Item
              label="Existing Razorpay plan id (optional)"
              name="plan_code"
              extra="If API keys fail, paste plan_xxx from Razorpay Dashboard → Subscriptions → Plans"
            >
              <Input placeholder="plan_xxxxxxxxxxxxx" />
            </Form.Item>
            <Form.Item label="Tier" name="tier">
              <Select
                allowClear
                placeholder="Optional tier"
                options={[
                  { value: 'STARTER', label: 'STARTER' },
                  { value: 'GROW', label: 'GROW' },
                  { value: 'PRO', label: 'PRO' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Amount (INR)"
              name="amount"
              rules={[{ required: true, message: 'Enter amount' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} placeholder="699" />
            </Form.Item>
            <Form.Item label="Validity" name="validity">
              <Select
                options={[
                  { value: '1 month', label: '1 month' },
                  { value: '6 months', label: '6 months' },
                  { value: '1 year', label: '1 year' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Sort order" name="sortOrder">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Divider orientation="left">Limits (-1 = unlimited)</Divider>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
            }}
          >
            <Form.Item label="Max active coupons" name="maxActiveCoupons">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Max shops" name="maxShops">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Happy Hours / month" name="happyHoursPerMonth">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Lucky Draws / month" name="luckyDrawsPerMonth">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Max staff" name="maxStaff">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </div>

          <Divider orientation="left">Feature flags</Divider>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <Form.Item
              label="AI suggestions"
              name="aiCampaignSuggestions"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Priority support"
              name="prioritySupport"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Featured placement"
              name="featuredPlacement"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Highlight (popular)"
              name="highlighted"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={creating}
            >
              Create Plan
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={selected ? `${selected.name} details` : 'Plan details'}
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Close
          </Button>,
        ]}
        width={640}
      >
        {selected && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Tier">
              {selected.tier ? <Tag color="blue">{selected.tier}</Tag> : '—'}
              {selected.highlighted ? (
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  Popular
                </Tag>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Price">
              ₹{selected.price} / {selected.validity}
            </Descriptions.Item>
            <Descriptions.Item label="Plan code">
              {selected.plan_code}
            </Descriptions.Item>
            <Descriptions.Item label="Active coupons">
              {limitLabel(selected.maxActiveCoupons)}
            </Descriptions.Item>
            <Descriptions.Item label="Shops">
              {limitLabel(selected.maxShops)}
            </Descriptions.Item>
            <Descriptions.Item label="Happy Hours / month">
              {limitLabel(selected.happyHoursPerMonth)}
            </Descriptions.Item>
            <Descriptions.Item label="Lucky Draws / month">
              {limitLabel(selected.luckyDrawsPerMonth)}
            </Descriptions.Item>
            <Descriptions.Item label="Staff">
              {limitLabel(selected.maxStaff)}
            </Descriptions.Item>
            <Descriptions.Item label="AI suggestions">
              {boolTag(selected.aiCampaignSuggestions)}
            </Descriptions.Item>
            <Descriptions.Item label="Priority support">
              {boolTag(selected.prioritySupport)}
            </Descriptions.Item>
            <Descriptions.Item label="Featured placement">
              {boolTag(selected.featuredPlacement)}
            </Descriptions.Item>
            <Descriptions.Item label="Shared inclusions">
              <Space wrap size={[4, 4]}>
                <Tag>Partner shops</Tag>
                <Tag>Visit rewards</Tag>
                <Tag>Festival templates</Tag>
                <Tag>Notifications</Tag>
                <Tag>Analytics</Tag>
                <Tag>ROI report</Tag>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PlanManagement;
